import type { BillingApiError } from "../client"

const MAX_RETRIES = 2

// The service asking to be tried later: 408 and 429 say so outright, and a 5xx
// is the service failing rather than the request being wrong. An error carrying
// no status never reached the service at all — a dropped connection — which is
// the most transient case of all.
const isRetryableStatus = (status: number | undefined): boolean =>
  status === undefined || status === 408 || status === 429 || status >= 500

/**
 * Whether a failed billing request is worth trying again. A 503 is the service
 * saying "later", so it is retried: the status and usage notices read from these
 * queries stay blank while they are failing, and the banner is mounted across
 * the shell, so a terminal verdict on a transient outage hides the notices
 * everywhere until something else refetches. Every other 4xx describes this
 * request rather than the service — no session, an organization the caller is
 * not in, a malformed call — so repeating it only spends a request to be told
 * the same thing, which the shell-wide mount would spend on every signed-in
 * session at once.
 */
export function shouldRetryBillingQuery(
  failureCount: number,
  error: BillingApiError,
): boolean {
  if (!isRetryableStatus(error.status)) {
    return false
  }
  return failureCount < MAX_RETRIES
}
